/**
 * Requires Legal Consent Guard (HU-AUTH-005, ADR-SEC-018).
 *
 * Corre DESPUÉS de `RequiresAuthGuard`. Para un usuario con sesión, en una ruta
 * protegida, pide al store del consentimiento que decida: si el usuario no ha
 * aceptado la versión vigente, el store abre el modal (montado en `App.vue`) y
 * esta navegación queda **pendiente** hasta que acepte (`true`) o salga (→ `/login`).
 *
 * No re-lee en cada navegación: el store guarda el resultado por `uid` para la
 * sesión, así que el costo es al iniciar o restaurar sesión.
 *
 * Exentas: las rutas públicas (`meta.requiresAuth === false`: login, registro,
 * recuperar contraseña) y `/verify-email`, que es parte del registro.
 * @see specs/HISTORIAS/auth-005-aceptar-terminos.md
 */

import type { RouteLocationNormalized } from 'vue-router'
import type { RouteGuard, GuardResult } from './RouteGuard'
import { auth, authReady } from '@/services/firebase/config'
import { useLegalConsentStore } from '@/stores/legalConsent'

export const LEGAL_CONSENT_EXEMPT_ROUTES = new Set(['verify-email'])

export class RequiresLegalConsentGuard implements RouteGuard {
  name = 'requiresLegalConsent'

  async canActivate(to: RouteLocationNormalized): Promise<GuardResult> {
    if (to.meta.requiresAuth === false) return true
    if (typeof to.name === 'string' && LEGAL_CONSENT_EXEMPT_ROUTES.has(to.name)) return true

    await authReady
    const uid = auth.currentUser?.uid
    if (!uid) return true // RequiresAuthGuard ya redirigió a /login

    const outcome = await useLegalConsentStore().evaluate(uid)
    return outcome === 'ok' ? true : '/login'
  }
}
